import { components, paths } from "@/api-schema";
import {
  PathsV1TemporaryPermissionSearchPostParametersQueryExtend,
  PathsV1TemporaryPermissionSearchPostParametersQueryOrderByDirection,
  PathsV1TemporaryPermissionSearchPostParametersQueryOrderByField,
  PathsV1TemporaryPermissionSearchPostParametersQuerySelect,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE as _DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from "@/common/api/_defaults";
import { useTemporaryPermissionSearchQuery } from "@/common/api/temporary-permission/use-temporary-permission-search.query";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { commonColumns } from "@/components/data-table/predefined/_constants/common-columns";
import { TemporaryPermissionTable } from "@/components/data-table/predefined/temporary-permission-table";
import { temporaryPermissionTableDefaultColumns } from "@/components/data-table/predefined/temporary-permission-table-columns";
import type { ImplementedColumnDef } from "@/components/data-table/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { buttonVariants } from "@/components/ui/button";
import { userStore } from "@/store/user-store";
import { PlusCircleIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

type TemporaryPermission = components["schemas"]["TemporaryPermission"] & RowWithIdentifier;
type SortingType = NonNullable<paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"]["orderBy"]>;

export function RequestView() {
  const { t } = useTranslation(["ram", "common"]);
  const { user } = userStore();
  const [paginationState, setPaginationState] = useState({
    page: DEFAULT_PAGE,
    pageSize: _DEFAULT_PAGE_SIZE,
  });
  const [sortingState, setSortingState] = useState<SortingType>([
    {
      field: PathsV1TemporaryPermissionSearchPostParametersQueryOrderByField.CreatedAt,
      direction: PathsV1TemporaryPermissionSearchPostParametersQueryOrderByDirection.Desc,
    },
  ]);
  const parameters = useMemo<paths["/v1/temporary-permission/search"]["post"]["parameters"]["query"]>(
    () => ({
      extend: [
        PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestFor,
        PathsV1TemporaryPermissionSearchPostParametersQueryExtend.RequestBy,
      ],
      orderBy: sortingState,
      page: paginationState.page,
      pageSize: paginationState.pageSize,
      select: [
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.Uuid,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.CreatedAt,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.Reason,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.RequestFor,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.Status,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.ValidFrom,
        PathsV1TemporaryPermissionSearchPostParametersQuerySelect.ValidUntil,
      ],
    }),
    [sortingState, paginationState],
  );

  const { data, isPending } = useTemporaryPermissionSearchQuery({
    options: {
      body: {
        request_by: [user.uuid],
      },
      parameters,
    },
  });
  const tableData = useMemo(() => data?.data || [], [data?.data]);
  const totalItems = data?.meta?.total ?? 0;

  const handlePaginationChange = useCallback((pagination: { page: number; pageSize: number }) => {
    setPaginationState(pagination);
  }, []);

  const handleSortingChange = useCallback((sorting: Array<{ field: string; direction: "asc" | "desc" }>) => {
    const typedSorting = sorting.map((s) => ({
      field: s.field,
      direction: s.direction,
    })) as SortingType;
    setSortingState(typedSorting);
  }, []);

  // Use only the columns we want to show for the request view
  const requestViewColumns = useMemo<ImplementedColumnDef<TemporaryPermission>[]>(
    () => [
      temporaryPermissionTableDefaultColumns.columnRequestFor as ImplementedColumnDef<TemporaryPermission>,
      temporaryPermissionTableDefaultColumns.columnValidFrom as ImplementedColumnDef<TemporaryPermission>,
      temporaryPermissionTableDefaultColumns.columnValidUntil as ImplementedColumnDef<TemporaryPermission>,
      {
        ...commonColumns.columnCreatedAt<TemporaryPermission>(),
        header: t("ram:RequestedOn"),
        cell: (row) =>
          automaticCellRenderer(row.getValue(), {
            dateFormat: "date",
          }),
      },
      temporaryPermissionTableDefaultColumns.columnStatus as ImplementedColumnDef<TemporaryPermission>,
    ],
    [t],
  );

  const headerComponent = useMemo(
    () => (
      <div className="flex flex-wrap gap-2">
        <Link href="~/ram/temporary/requests/create" className={buttonVariants()}>
          <PlusCircleIcon /> {t("ram:RequestTemporaryPermissions")}
        </Link>
      </div>
    ),
    [t],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:TemporaryPermissions")}
      subTitle={t("ram:TemporaryPermissionsDescription")}
    >
      <TemporaryPermissionTable
        data={tableData}
        columns={requestViewColumns}
        isLoading={isPending}
        autoFetch={false}
        totalItems={totalItems}
        page={paginationState.page}
        pageSize={paginationState.pageSize}
        serverSidePagination={true}
        features={{
          multiSelection: false,
          pagination: true,
          search: true,
          expansion: true,
        }}
        headerComponent={headerComponent}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
      />
    </CenteredLayout>
  );
}
